import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

/**
 * PatientInfoCard — Slim patient summary strip shown at the top of forms.
 * Compact single-line: initials pill · Full Name · DOB · email
 * Designed to use minimal vertical space.
 */
export default function PatientInfoCard({ leadData, practiceName }) {
    if (!leadData) return null;

    const firstName = leadData.first_name || '';
    const lastName = leadData.last_name || '';
    const initials = ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() || '?';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    // Format DOB: YYYY-MM-DD → DD/MM/YYYY
    let dobDisplay = '';
    if (leadData.date_of_birth) {
        const parts = leadData.date_of_birth.split('-');
        if (parts.length === 3) {
            dobDisplay = `${parts[2]}/${parts[1]}/${parts[0]}`;
        } else {
            dobDisplay = leadData.date_of_birth;
        }
    }

    // Build metadata chips
    const meta = [];
    if (dobDisplay) meta.push(dobDisplay);
    if (leadData.email) meta.push(leadData.email);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1,
            mb: 2,
            borderRadius: '8px',
            bgcolor: '#F8F9FA',
            border: '1px solid rgba(145,158,171,0.12)',
            minHeight: 40,
            flexWrap: 'wrap',
        }}>
            {/* Initials pill */}
            <Box sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: '#00AB55',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 600,
                flexShrink: 0,
                letterSpacing: '0.3px',
            }}>
                {initials}
            </Box>

            {/* Name */}
            <Typography sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#212B36',
                lineHeight: 1,
                whiteSpace: 'nowrap',
            }}>
                {fullName || 'Patient'}
            </Typography>

            {/* Metadata — inline, smaller text */}
            {meta.length > 0 && (
                <Typography sx={{
                    fontSize: '12px',
                    color: '#919EAB',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {meta.join('  ·  ')}
                </Typography>
            )}

            {/* Practice name — far right, hidden on mobile */}
            {practiceName && (
                <Typography sx={{
                    ml: 'auto',
                    fontSize: '11px',
                    color: '#919EAB',
                    lineHeight: 1,
                    display: { xs: 'none', sm: 'block' },
                    whiteSpace: 'nowrap',
                }}>
                    {practiceName}
                </Typography>
            )}
        </Box>
    );
}

PatientInfoCard.propTypes = {
    leadData: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
        date_of_birth: PropTypes.string,
    }),
    practiceName: PropTypes.string,
};

PatientInfoCard.defaultProps = {
    leadData: null,
    practiceName: '',
};
